import React from 'react'
import { Lightbulb, Code, TestTube, Rocket, Settings, BarChart3 } from 'lucide-react'

const WorkflowSection = () => {
  const steps = [
    {
      icon: Lightbulb,
      title: 'Discovery & Planning',
      description: 'We analyze your business needs, define objectives, and create a comprehensive AI strategy tailored to your goals.',
      color: 'from-blue-500 to-blue-600',
      number: '01'
    },
    {
      icon: Code,
      title: 'AI Development',
      description: 'Our expert team builds custom AI models using cutting-edge machine learning and natural language processing technologies.',
      color: 'from-purple-500 to-purple-600',
      number: '02'
    },
    {
      icon: TestTube,
      title: 'Testing & Training',
      description: 'Rigorous testing and continuous training ensure your AI agents deliver accurate, context-aware responses every time.',
      color: 'from-green-500 to-green-600',
      number: '03'
    },
    {
      icon: Rocket,
      title: 'Deployment',
      description: 'Seamless integration with your existing systems and smooth deployment across all your customer touchpoints.',
      color: 'from-orange-500 to-orange-600',
      number: '04'
    },
    {
      icon: Settings,
      title: 'Optimization',
      description: 'Continuous monitoring and optimization based on real-world performance data to maximize efficiency and accuracy.',
      color: 'from-red-500 to-red-600',
      number: '05'
    },
    {
      icon: BarChart3,
      title: 'Analytics & Insights',
      description: 'Comprehensive reporting and actionable insights help you understand customer behavior and improve service quality.',
      color: 'from-indigo-500 to-indigo-600',
      number: '06'
    }
  ]

  return (
    <section data-component-path="client/src/components/home/WorkflowSection.jsx" data-component-line="54:4" data-component-id="client/src/components/home/WorkflowSection.jsx:54:4" className="py-24 bg-gradient-to-br from-gray-50 via-blue-50 to-gray-50 relative overflow-hidden">
      {/* Background decoration */}
      <div data-component-path="client/src/components/home/WorkflowSection.jsx" data-component-line="56:6" data-component-id="client/src/components/home/WorkflowSection.jsx:56:6" className="absolute inset-0 opacity-5">
        <div data-component-path="client/src/components/home/WorkflowSection.jsx" data-component-line="57:8" data-component-id="client/src/components/home/WorkflowSection.jsx:57:8" className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500 rounded-full blur-3xl"></div>
        <div data-component-path="client/src/components/home/WorkflowSection.jsx" data-component-line="58:8" data-component-id="client/src/components/home/WorkflowSection.jsx:58:8" className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500 rounded-full blur-3xl"></div>
      </div>

      <div data-component-path="client/src/components/home/WorkflowSection.jsx" data-component-line="61:6" data-component-id="client/src/components/home/WorkflowSection.jsx:61:6" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div data-component-path="client/src/components/home/WorkflowSection.jsx" data-component-line="62:8" data-component-id="client/src/components/home/WorkflowSection.jsx:62:8" className="text-center mb-20">
          <div data-component-path="client/src/components/home/WorkflowSection.jsx" data-component-line="63:10" data-component-id="client/src/components/home/WorkflowSection.jsx:63:10" className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
            <Settings data-component-path="client/src/components/home/WorkflowSection.jsx" data-component-line="64:12" data-component-id="client/src/components/home/WorkflowSection.jsx:64:12" className="w-4 h-4" />
            Our Process
          </div>
          <h2 data-component-path="client/src/components/home/WorkflowSection.jsx" data-component-line="67:10" data-component-id="client/src/components/home/WorkflowSection.jsx:67:10" className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            AI Workflow Development Process
          </h2>
          <p data-component-path="client/src/components/home/WorkflowSection.jsx" data-component-line="70:10" data-component-id="client/src/components/home/WorkflowSection.jsx:70:10" className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            From concept to deployment, our proven methodology ensures your AI agents deliver exceptional results at every stage
          </p>
        </div>

        <div data-component-path="client/src/components/home/WorkflowSection.jsx" data-component-line="75:8" data-component-id="client/src/components/home/WorkflowSection.jsx:75:8" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {steps.map((step, index) => {
            const Icon = step.icon
            return (
              <div data-component-path="client/src/components/home/WorkflowSection.jsx" data-component-line="79:14" data-component-id="client/src/components/home/WorkflowSection.jsx:79:14"
                key={index}
                className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-blue-200"
              >
                {/* Step number badge */}
                <div data-component-path="client/src/components/home/WorkflowSection.jsx" data-component-line="84:16" data-component-id="client/src/components/home/WorkflowSection.jsx:84:16" className="absolute -top-4 -right-4 w-12 h-12 bg-gradient-to-br from-gray-800 to-gray-900 rounded-full flex items-center justify-center shadow-lg">
                  <span data-component-path="client/src/components/home/WorkflowSection.jsx" data-component-line="85:18" data-component-id="client/src/components/home/WorkflowSection.jsx:85:18" className="text-white font-bold text-sm">{step.number}</span>
                </div>

                {/* Icon with gradient background */}
                <div data-component-path="client/src/components/home/WorkflowSection.jsx" data-component-line="89:16" data-component-id="client/src/components/home/WorkflowSection.jsx:89:16" className={`w-16 h-16 rounded-xl bg-gradient-to-br ${step.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-md`}>
                  <Icon data-component-path="client/src/components/home/WorkflowSection.jsx" data-component-line="90:18" data-component-id="client/src/components/home/WorkflowSection.jsx:90:18" className="w-8 h-8 text-white" />
                </div>

                <h3 data-component-path="client/src/components/home/WorkflowSection.jsx" data-component-line="93:16" data-component-id="client/src/components/home/WorkflowSection.jsx:93:16" className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-blue-600 transition-colors">
                  {step.title}
                </h3>

                <p data-component-path="client/src/components/home/WorkflowSection.jsx" data-component-line="97:16" data-component-id="client/src/components/home/WorkflowSection.jsx:97:16" className="text-gray-600 leading-relaxed">
                  {step.description}
                </p>

                {/* Decorative element */}
                <div data-component-path="client/src/components/home/WorkflowSection.jsx" data-component-line="102:16" data-component-id="client/src/components/home/WorkflowSection.jsx:102:16" className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-blue-500 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
            )
          })}
        </div>

        {/* Bottom CTA */}
        <div data-component-path="client/src/components/home/WorkflowSection.jsx" data-component-line="109:8" data-component-id="client/src/components/home/WorkflowSection.jsx:109:8" className="mt-16 text-center">
          <div data-component-path="client/src/components/home/WorkflowSection.jsx" data-component-line="110:10" data-component-id="client/src/components/home/WorkflowSection.jsx:110:10" className="inline-flex flex-col sm:flex-row gap-4 items-center">
            <a data-component-path="client/src/components/home/WorkflowSection.jsx" data-component-line="111:12" data-component-id="client/src/components/home/WorkflowSection.jsx:111:12"
              href="/pricing"
              className="h-14 px-8 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 flex items-center justify-center gap-2 transition-all duration-200 shadow-lg hover:shadow-xl font-semibold text-lg"
            >
              Start Your AI Journey
              <Rocket data-component-path="client/src/components/home/WorkflowSection.jsx" data-component-line="116:14" data-component-id="client/src/components/home/WorkflowSection.jsx:116:14" className="w-5 h-5" />
            </a>
            <a data-component-path="client/src/components/home/WorkflowSection.jsx" data-component-line="118:12" data-component-id="client/src/components/home/WorkflowSection.jsx:118:12"
              href="#features"
              className="h-14 px-8 bg-white text-gray-900 rounded-lg border-2 border-gray-200 hover:border-blue-600 flex items-center justify-center gap-2 transition-all duration-200 shadow-md hover:shadow-lg font-semibold text-lg"
            >
              Explore Features
            </a>
          </div>
        </div>
      </div>
    </section>
  )
}

export default WorkflowSection